import { Dialog, Grid, Slide, useMediaQuery } from "@mui/material"
import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import SpeedDial from "@mui/material/SpeedDial"
import SpeedDialAction from "@mui/material/SpeedDialAction"
import SpeedDialIcon from "@mui/material/SpeedDialIcon"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import { getAnalytics } from "firebase/analytics"
import {
  and,
  collection,
  DocumentData,
  getFirestore,
  onSnapshot,
  or,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import { getAccountFromDocSnapshot } from "../contexts/AccountContext"
import {
  getEmptyRecord,
  getRecordFromDocSnapshot,
} from "../contexts/RecordContext"
import { getRecordTypeFromDocSnapshot } from "../contexts/RecordTypeContext"
import { Collection, Field, Filters } from "../Enum"
import {
  paperStyleDefault,
  redGreenCellColor,
  useSpeedDialStyle,
} from "../Style"
import { timber_debug } from "../Timber"
import { Account, Record, RecordType, UserInfo } from "../Type"
import {
  getRecordListColumns,
  getTranslatedDataGridToolbar,
} from "../utils/DataGridUtils"
import { exportRecordsAsExcel } from "../utils/ExcelUtils"
import { exportRecordListAsPdf } from "../utils/PdfUtils"
import { Action, getRecordListSpeedDialActions } from "../utils/SpeedDialUtils"
import PieChart from "./PieChart"
import RecordDialog from "./RecordDialog"
import RecordTypeDialog from "./RecordTypeDialog"

const RecordList: FunctionComponent<{ userInfo: UserInfo }> = ({
  userInfo,
}) => {
  const intl = useIntl()
  // Keeps action tooltip text in a single line
  const classes = useSpeedDialStyle()
  const minHeight = useMediaQuery("(min-height:700px)")
  const minWidth = useMediaQuery("(min-width:900px)")
  const analytics = getAnalytics()

  const accountAnonymous = intl.formatMessage({
    id: "anonymous",
  })

  const [recordFilter, setRecordFilter] = useState<string>(Filters.WEEK)
  const [records, setRecords] = useState<Record[]>([])
  const [recordTypes, setRecordTypes] = useState<RecordType[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [selectedAccount, setSelectedAccount] = useState<Account>()
  const [startDate, setStartDate] = useState<number>(
    dayjs().startOf("week").valueOf()
  )
  const [endDate, setEndDate] = useState<number>(
    dayjs().endOf("week").valueOf()
  )

  // Dialog states
  const [openRecDialog, setOpenRecDialog] = useState<boolean>(false)
  const [openRecTypeDialog, setOpenRecTypeDialog] = useState<boolean>(false)
  const [record, setRecord] = useState<Record>(
    getEmptyRecord(selectedAccount, recordTypes)
  )

  // Firestore
  const firestore = getFirestore()
  const accountsRef = collection(
    firestore,
    `/${Collection.Users}/${userInfo.uid}/${Collection.Accounts}`
  )
  const recordsRef = collection(
    firestore,
    `/${Collection.Users}/${userInfo.uid}/${Collection.Records}`
  )
  const recordTypesRef = collection(
    firestore,
    `/${Collection.Users}/${userInfo.uid}/${Collection.RecordTypes}`
  )
  const recQueryConditions = [
    where(Field.Date, ">", startDate),
    where(Field.Date, "<", endDate),
  ]

  const recordsQuery = selectedAccount
    ? query(
        recordsRef,
        and(
          ...recQueryConditions,
          or(
            where(Field.AccountID, "==", selectedAccount.id),
            where(Field.Account, "==", selectedAccount.name)
          )
        )
      )
    : query(recordsRef, ...recQueryConditions)
  const recordTypesQuery = query(recordTypesRef, orderBy(Field.Name))
  const accountsQuery = query(accountsRef, orderBy(Field.Name))

  /**
   * Sets up real-time Account listener
   */
  useEffect(() => {
    timber_debug("useEffect subscribeAccountsListener")
    const unsubscribeAccountsListener = onSnapshot(
      accountsQuery,
      (querySnapshot) => {
        timber_debug(
          `${querySnapshot.size} real-time Account snapshots received`
        )
        const newAccounts: Account[] = []
        querySnapshot.forEach(
          (doc: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
            newAccounts.push(getAccountFromDocSnapshot(accountAnonymous, doc))
          }
        )
        timber_debug(`Downloaded ${newAccounts.length} newAccounts`)
        setAccounts(newAccounts)
        setSelectedAccount(newAccounts[0])
      },
      (error) => {
        console.error("Error fetching accounts", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeAccountListener")
      unsubscribeAccountsListener()
    }
  }, [])

  /**
   * Sets up real-time RecordType listener
   */
  useEffect(() => {
    timber_debug("useEffect subscribeRecTypeListener")
    const unsubscribeRecTypeListener = onSnapshot(
      recordTypesQuery,
      (querySnapshot) => {
        timber_debug(
          `${querySnapshot.size} real-time RecType snapshots received`
        )
        const newRecordTypes: RecordType[] = []
        querySnapshot.forEach(
          (doc: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
            newRecordTypes.push(getRecordTypeFromDocSnapshot(doc))
          }
        )
        timber_debug(`Downloaded ${newRecordTypes.length} recordTypes`)
        setRecordTypes(newRecordTypes)
      },
      (error) => {
        console.error("Error fetching recordTypes", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeRecTypeListener")
      unsubscribeRecTypeListener()
    }
  }, [])

  /**
   * Sets up real-time Record listener
   */
  useEffect(
    () => {
      timber_debug("useEffect subscribeRecordListener")

      let unsubscribeRecordListener = () => {}
      if (selectedAccount && recordTypes.length > 0) {
        timber_debug(
          `Query account ${selectedAccount?.name} startDate ${dayjs(
            startDate
          )} endDate ${dayjs(endDate)}`
        )

        unsubscribeRecordListener = onSnapshot(
          recordsQuery,
          (querySnapshot) => {
            timber_debug(
              `${querySnapshot.size} real-time Record snapshots received`
            )
            const newRecords: Record[] = []
            querySnapshot.forEach(
              (doc: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
                newRecords.push(getRecordFromDocSnapshot(doc))
              }
            )
            timber_debug(`Downloaded ${newRecords.length} records`)
            setRecords(newRecords)
          },
          (error) => {
            console.error("Error fetching records", error)
          }
        )
      }

      return () => {
        timber_debug("useEffect unsubscribeRecordListener")
        unsubscribeRecordListener()
      }
    },
    [
      selectedAccount,
      recordTypes,
      startDate,
    ] /* No need reload for endDate change because both start and end dates are changed simultaneously */
  )

  const filterRecords = (event: SelectChangeEvent) => {
    // Display seleted filter in the Selection box
    setRecordFilter(event.target.value)
    // console.log("Selected filter " + event.target.value)
    switch (event.target.value) {
      case Filters.WEEK:
        setStartDate(dayjs().startOf("week").valueOf())
        setEndDate(dayjs().endOf("week").valueOf())
        break
      case Filters.MONTH:
        setStartDate(dayjs().startOf("month").valueOf())
        setEndDate(dayjs().endOf("month").valueOf())
        break
      case Filters.LAST_MONTH:
        setStartDate(dayjs().subtract(1, "month").startOf("month").valueOf())
        // Get records which were created before the first day of this month
        setEndDate(dayjs().subtract(1, "month").endOf("month").valueOf())
        break
      case Filters.YEAR:
        setStartDate(dayjs().startOf("year").valueOf())
        setEndDate(dayjs().endOf("year").valueOf())
        break

      default:
        break
    }
  }

  const selectRecord = (record: Record) => {
    setRecord(record)
    setOpenRecDialog(true)
  }

  const openRecordDialog = () => {
    // Reset record before opening dialog
    setRecord({
      id: "",
      date: dayjs().valueOf(),
      amount: 0,
      credit: true,
      recordTypeId: recordTypes.length !== 0 ? recordTypes[0].id : "",
      insertDate: dayjs().valueOf(),
      lastEditDate: dayjs().valueOf(),
      note: "",
    } as Record)
    setOpenRecDialog(true)
  }

  const handleFabAction = (actionId: string) => {
    switch (actionId) {
      case "record":
        openRecordDialog()
        break
      case "recordType":
        setOpenRecTypeDialog(true)
        break
      case "pdf":
        exportRecordListAsPdf(records, recordTypes, analytics)
        break
      case "excel":
        exportRecordsAsExcel(records, recordTypes)
        break
      default:
        break
    }
  }

  return (
    <Box
      height="91vh"
      display="flex"
      flexDirection="column"
      sx={redGreenCellColor}
    >
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <Grid
          container
          spacing={minHeight ? 1 : 0}
          direction={"row"}
          height="85vh"
        >
          {/* Left side: Pie chart & account selector. NOTE: Adjust Pie chart size here */}
          <Grid
            container
            alignItems={"center"}
            size={{ xs: 12, sm: 12, md: 4, lg: 4 }}
          >
            {/* Account filter dropdownlist */}
            {selectedAccount && (
              <Grid marginTop={minWidth ? -16.5 : 0} size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel id="label-account-filter">
                    <FormattedMessage id="filters.account" />
                  </InputLabel>
                  <Select
                    fullWidth
                    value={selectedAccount.name}
                    label={intl.formatMessage({
                      id: "filters.account",
                    })}
                    onChange={(event: SelectChangeEvent) => {
                      let accountName = event.target.value
                      // Display selected account in the Selection box
                      setSelectedAccount(
                        accounts.find((account: Account) => {
                          return account.name === accountName
                        })
                      )
                      timber_debug(
                        "filterAccounts selectedAccount: " + accountName
                      )
                    }}
                  >
                    {accounts.map((account, index) => (
                      <MenuItem key={index} value={account.name}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Do not adjust Pie chart size here i.e., it should be set to 12 for sm, md, lg */}
            <Grid
              minWidth={350}
              height={minHeight ? 350 : 250}
              size={{ sm: 12, md: 12, lg: 12 }}
            >
              <PieChart records={records} recordTypes={recordTypes} />
            </Grid>
          </Grid>

          {/* Right side */}
          <Grid
            style={{ marginTop: minHeight ? 20 : 0 }}
            size={{ xs: 12, sm: 12, md: 8, lg: 8 }}
          >
            {/* Record filter dropdownlist */}
            <FormControl fullWidth>
              <InputLabel id="label-record-filter">
                <FormattedMessage id="filters" />
              </InputLabel>
              <Select
                labelId="label-record-filter"
                value={recordFilter}
                label={intl.formatMessage({
                  id: "filters",
                })}
                onChange={filterRecords}
              >
                <MenuItem value={Filters.WEEK}>
                  <FormattedMessage id="filters.week" />
                </MenuItem>
                <MenuItem value={Filters.MONTH}>
                  <FormattedMessage id="filters.month.current" />
                </MenuItem>
                <MenuItem value={Filters.LAST_MONTH}>
                  <FormattedMessage id="filters.month.last" />
                </MenuItem>
                <MenuItem value={Filters.YEAR}>
                  <FormattedMessage id="filters.year" />
                </MenuItem>
                {/* <MenuItem value={Filters.CUSTOM}>
                  <FormattedMessage id="filters.custom" />
                </MenuItem> */}
              </Select>
            </FormControl>

            {/* Record list */}
            <div style={{ height: minHeight ? "85%" : "100%" }}>
              <DataGrid
                localeText={getTranslatedDataGridToolbar(intl)}
                onRowClick={(params) => selectRecord(params.row as Record)}
                rows={records}
                columns={getRecordListColumns(intl, recordTypes)}
                pageSizeOptions={[10]}
                disableRowSelectionOnClick
                density={minHeight ? "standard" : "compact"}
                showToolbar
              />
            </div>
          </Grid>
        </Grid>

        {/* Action button */}
        <SpeedDial
          ariaLabel="Bookkeepr SpeedDial"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
          icon={<SpeedDialIcon />}
          FabProps={{
            style: { backgroundColor: Color.Red40, color: Color.White },
          }}
        >
          {getRecordListSpeedDialActions(intl).map((action: Action) => (
            <SpeedDialAction
              key={action.id}
              icon={action.icon}
              slotProps={{
                tooltip: {
                  title: action.name,
                  open: true,
                },
                fab: {
                  style: {
                    backgroundColor: Color.Primary,
                    color: Color.Black,
                  },
                },
              }}
              classes={classes} // for keep action tooltip text in a single line
              onClick={() => {
                handleFabAction(action.id)
              }}
            />
          ))}
        </SpeedDial>

        {/* Record dialog */}
        <Dialog
          open={openRecDialog}
          slots={{ transition: Slide }}
          slotProps={{
            transition: {
              direction: "up",
            },
          }}
          keepMounted
          onClose={(_, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              setOpenRecDialog(false)
            }
          }}
        >
          <RecordDialog
            userId={userInfo.uid}
            record={record}
            recordTypes={recordTypes}
            onClickClose={() => {
              setOpenRecDialog(false)
            }}
            selectedAccount={selectedAccount!!}
          ></RecordDialog>
        </Dialog>

        {/* Record Type dialog */}
        <Dialog
          open={openRecTypeDialog}
          slots={{ transition: Slide }}
          slotProps={{
            transition: {
              direction: "up",
            },
          }}
          keepMounted
          onClose={(_, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              setOpenRecTypeDialog(false)
            }
          }}
        >
          <RecordTypeDialog
            userId={userInfo.uid}
            recordTypes={recordTypes}
            onClickClose={() => {
              setOpenRecTypeDialog(false)
            }}
          ></RecordTypeDialog>
        </Dialog>
      </Paper>
    </Box>
  )
}

export default RecordList
